import React, { useState } from 'react';
import { Star, X } from 'lucide-react';
import axios from 'axios';

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookRequestId: string;
  bookTitle: string;
  userName: string;
  onRatingSubmitted: () => void;
}

const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
  bookRequestId,
  bookTitle,
  userName,
  onRatingSubmitted
}) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Veuillez sélectionner une note');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('/api/ratings', {
        book_request_id: bookRequestId,
        rating,
        comment
      });

      onRatingSubmitted();
      onClose();
      setRating(0);
      setComment('');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Erreur lors de l\'ajout de l\'avis');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Noter l'échange
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-gray-600 mb-2">
            <strong>Livre :</strong> {bookTitle}
          </p>
          <p className="text-gray-600">
            <strong>Avec :</strong> {userName}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Votre note
            </label>
            <div className="flex space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`p-1 ${
                    star <= rating ? 'text-yellow-400' : 'text-gray-300'
                  } hover:text-yellow-400 transition-colors`}
                >
                  <Star className="h-8 w-8 fill-current" />
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {rating > 0 && (
                <>
                  {rating === 1 && 'Très mauvais'}
                  {rating === 2 && 'Mauvais'}
                  {rating === 3 && 'Moyen'}
                  {rating === 4 && 'Bien'}
                  {rating === 5 && 'Excellent'}
                </>
              )}
            </p>
          </div>

          <div className="mb-6">
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
              Commentaire (optionnel)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="input h-24 resize-none"
              placeholder="Partagez votre expérience..."
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">
              {comment.length}/500 caractères
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={loading || rating === 0}
              className="btn btn-primary flex-1"
            >
              {loading ? 'Envoi...' : 'Envoyer l\'avis'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RatingModal; 